import { IReplaceText } from 'base/interfaces/common';
import { IProduct as IModProduct, IProductWholesale } from 'mod/product/interface';
import { postAddCart } from '@base/product/services'

export const PRODUCT_TAG_TYPES = [
    'SurpriseEgg',
    'RedeemRewards',
    'SlotMachine',
    'PointsMall',
    'Bonus',
    'XmasSale',
    'giftbox24',
    'superFlashSale',
    'wholesale',
    'flashSale',
    'bestSeller',
    'newArrival',
    'presale',
    'EarlyAccess',
] as const;

export type ProductTagType = typeof PRODUCT_TAG_TYPES[number];

export type IProductTag = {
    tag: string; // PRODUCT_TAG_TYPES中的值
}

export type IOffTagShow = 'OFF' | '-';

type ProductOptionValue = {
    optionValueId: number;
    name: string;
    productVariantIds: number[]
};

export type IProductOption = {
    productOptionId: number;
    productOptionName: string;
    productOptionValues: ProductOptionValue[];
}

export type IKitProduct = {
    kitName: string;
    kitScopeItemList: {
        num: number;
        productVariantId: string;
    }[];
    kitVariantId: string;
}

export type IProductVariantItem = {
    productVariantId: number;
    sku: string;
    shopPrice: string;
    exchangeShopPrice: string;
    marketPrice: string;
    exchangeMarketPrice: string;
    isOnSale: string;
    stockNum: string;
}

export const PRODUCT_KEYS = [
    'goodsId',
    'goodsLink',
    'picUrl',
    'goodsName',
    'goodsPrice',
    'goodsPriceText',
    'marketPrice',
    'marketPriceText',
    'score',
    'productOptionList',
    'productVariants',
];

export type ProductType = {
    goodsId?: number;
    goodsLink?: string;
    picUrl?: string | string[];
    goodsName?: string;
    goodsPrice?: number;
    goodsPriceText?: string;
    marketPrice?: number;
    marketPriceText?: string;
    score?: number;//旧版
    reviewTotal?: number;//旧版
    reviewScore?: string;//新版
    reviewNum?: number;//新版
    productOptionList?: IProductOption[];
    productVariants?: IProductVariantItem[];
}

export type IImage = {
    cover: string;
    isMain: number;
    isSpu: number;
    picId: string;
    picLink: string;
    altText: string;
    productVariantId: string;
    spuId: string;
    type: number;
    isListHover: 0 | 1;
}


export type IActivityCode =
    'FLASHSALE' |
    'SUPER_FLASHSALE' |
    'GIFTBOX_WINTER_SALE' |
    'POINTS_MALL_FLASH_SALE' |
    'SLOT_MACHINE'

export type IProductActivity = {
    valid: boolean; //活动是否有效
    code: IActivityCode; //ActivityCode, enum:[ FLASHSALE, SUPER_FLASHSALE, GIFTBOX_WINTER_SALE, POINTS_MALL_FLASH_SALE ]
    text: string; //倒计时上方显示的活动名
    timestamp: number; //second
    percent: number; //百分比
    progressBarText: string; //进度条文案
    userReachedPurchaseLimit: boolean; //用户是否达到限购额度, true:达到限购额度
    activityReachedStockLimit: boolean; //活动库存, true:库存不足
}


export type IKitProductVariant = {
    desc: string;
    kitNum: number;
    name: string;
    pics: IImage[],
    productId: string;
    productVariantId: string;
    shopPrice: number;
    sku: string;
    url: string;
}

export type IManualItem = {
    brief: string;
    link: string;
    name: string;
    productId: string;
    productVariantId: string;
}
// pdf
export type IPdf = {
    link: string;
    name: string;
    fileName: string;
    productId: string;
    productVariantId: string;
}

export type IReview = {
    productVariantId: string;
    reviewNum: number;
    score: number;
}

export type IInitConfig = {
    flashSaleUrl: string;
    bundleDealsUrl: string;
}

export type IGift = {
    variantId: string;
    text: string;
    tip: string;
    default?: boolean;
};

export type IGifts = {
    activity: {
        title: string;
        triggerText: string;
    };
    detail: {
        drawTitle?: string;
        picture: {
            src: string;
            width: number;
            height: number;
        };
        label: string;
        options: Array<IGift>;
        btnText: string;
    }
};

export type IGiftItem = {
    variantId: string;
    num: number;
    title: string;
    pic: string;
};

// 单个variant（sku）属性
export type IVariant = {
    addOns: IProduct[];
    afterPayTips?: string;
    affirmPayTips?: string;
    afterPerDiscountPriceExchangeLocalSymbol?: string;
    afterPerDiscountPriceExchangeScale?: string;
    afterPerDiscountPriceExchangeSymbol?: string;
    afterPerDiscountPriceLocalSymbol: string; // 商品优惠后的价格
    afterPerDiscountPriceScale: string;
    afterPerDiscountPriceSymbol: string;
    bundleDealsVo: {
        bundleDeals: boolean;
        description?: IReplaceText;
        bundleDealsLink?: string;
    };
    categoryInfo: ICategoryInfo;
    categoryTrackCode: string;
    dealPrice: number;
    dealPriceDisplay?: string;
    dealPriceExchange: 0;
    dealPriceExchangeDisplay?: string;
    dealerShopPriceExchangeDisplay: string; //wholesale用户划线价
    deliveryTime?: string;
    desc?: string
    descMarkdown?: string;
    descMarkdownHtml: string;
    discountLocalPriceNumDisplay?: string;
    discountMode: 'none' | 'percent' | 'fixed'; // 优惠类型
    discountPriceNumDisplay: string; // 商品优惠值
    freeShipOrderAmount?: string;
    isOnSale: 1 | 0;
    klarnaPayTips?: string;
    kitProductVariantList: IKitProductVariant[];
    kitStockSellType?: string;
    marketPrice: number;
    marketPriceExchangeScale?: string;
    marketPriceExchangeSymbol?: string;
    marketPriceLocalSymbol?: string;
    marketPriceScale?: string;
    marketPriceSymbol?: string;
    name: string;
    pics: IImage[];
    productAttributeList: IProductAttributeListItem[];
    productId: string;
    productName: string;
    productManualList: IManualItem[];
    productPdfVoList: IPdf[];
    productSellingPoints: ISellingPoints[];
    productTagsVoList: IProductTag[];
    productTrackCode: string;
    productVariantId: string;
    review: IReview;
    savePrice?: number;
    seelWfdTip?: string; // 保单提示
    savePriceExchangeDisplay?: string;
    savePriceExchangeScale?: string;
    savePriceExchangeSymbol?: string;
    savePriceLocalSymbol?: string;
    savePriceScale?: string;
    savePriceSymbol?: string;
    shopPrice: number;
    shopPriceDisplay: string;
    shopPriceExchange: number;
    shopPriceExchangeDisplay: string;
    shopPriceExchangeLocalSymbol: string;
    shopPriceExchangeScale: string;
    shopPriceExchangeSymbol: string;
    shopPriceLocalSymbol: string;
    shopPriceScale: string;
    shopPriceSymbol: string;
    vat: boolean;
    sku: string;
    stockNum: number;
    showDiyEntry: boolean;
    trackId: string;
    url: string;
    variantId: string;
    variantTrackCode: string;
    warrantyAfterSaleTime?: string;
    warrantyFreeReturnTime?: string;
    weight?: number;
    flashSale: {
        timestamp: string;
        valid: boolean;
        flashSaleListLink: string
    },
    superFlashSale: {
        timestamp: string;
        valid: boolean;
    },
    points: {
        ratio: number;
        showTag: boolean;
        value: number;
    },
    gifts?: IGifts;
    disableActions?: Array<'addToCart' | 'buyNow'>;
    descModuleCode: string; //Description feature 列表 code，为空时加载 markdown
    giftItemList: Array<IGiftItem>;
    expectedArrivalTime: string;
} & IProductWholesale;

// 单个productOptionValue（option，最小选择单位）属性
export interface IOption {
    optionValueId: number;
    name: string;
    productVariantIds: string[];
    disabled?: boolean;     //  是否可点击
    outOfStock?: boolean;   //  是否有库存
}

// 尺码、颜色等选项
export interface IGroup {
    productOptionId: number;
    productOptionName: string;
    productOptionValues: IOption[];
    checkedOption?: IOption; //是否选中
}

//------------------------------------------------
export interface IOptionsSelected {
    [key: string]: string | number | undefined;
}

export interface ISkuObjInfoItem {
    opId: number;
    opName: string;
    opSkusIds: number[];
}

export interface ISkuObjInfo {
    [key: string]: ISkuObjInfoItem
}

export interface IClickOptionBack {
    availableSkusIdsArr: number[],
    selectedType: 'none' | 'some' | 'all',
    isSelectedAll: boolean;
    addCartStep: 'selecting' | 'selected';
    targetSku?: IVariant;
}

export interface IProductOptionListItem {
    productOptionId: number;
    productOptionName: string;
    productOptionValues: {
        optionValueId: number;
        name: string;
        productVariantIds: string[]
    }[];
}

export interface IAddCartItem {
    productVariantId?: string;
    num: number;
    usePerProductDiscount?: 0 | 1;
    mainVariant?: boolean; // 标记改变体为主变体。 该字段只在Buy together时有用，其余情况可不传。
}

export type IAddCartType = 'defaultAdd' | 'buyTogether' | 'bonusItemTradeIn';

// 分类
export interface ICategoryInfo {
    catId: string;
    catName: string;
    catLinkUrl: string;
    picUrl?: string;
    categoryTrackCode: string;
}

export interface IProductAttributeListItem {
    attributeId: string;
    attributeName: string;
    attributeOptionId: string;
    attributeOptionName: string;
    attributeValues: {
        attributeValueId: number;
        name: string;
    }[];
}

export interface ISellingPoints {
    productId: string;
    sort: number;
    textName: string;
    textType: number;
    textValue: string;
}

export interface ISpuInfo {
    id: string;
    productId: string;
    selectedVariantId: string;
    productOptionList: IGroup[];
    productVariants: IVariant[];
    productType?: 'kit' | 'product';
    unit: string;
    url: string;
    ageLimit: boolean; //是否跳转未成年确认页面
}

export interface IReviewInfo {
    score: string;
    reviewNum: number;
}

// buy together
export type ITogetherProduct = IModProduct;
export type ITogether = {
    productId: string;
    productVariantId: string;
    variantTogetherList: ITogetherProduct[];
}

export type ITogetherPrice = {
    discountPrice: number;          // 优惠金额
    discountPriceExchange: number;
    discountPriceExchangeDisplay: string;
    productTotalPrice: number;      // 商品原价
    productTotalPriceExchange: number;
    productTotalPriceExchangeDisplay: string;
    totalPrice: number;             // 优惠后金额
    totalPriceExchange: number;
    totalPriceExchangeDisplay: string;
}

// 推荐商品列表
export type IRelatedProductsItem = {
    spuId: string;
    skuId: string;
    price: string;
    imgUrl: string;
    name: string;
    isStock: number;
    link?: string;
}

export interface IShowPrice {
    shopPrice?: string;
    marketPrice?: string;
    savePrice?: string;
}

export type IProductSpu = {
    code?: string;
    spuInfo: ISpuInfo;
    productId?: string;
}

// 详情页服务端获取数据
export type ServerProps = {
    spu: ISpuInfo;
    errors: Error[];
}

export type IBuyNowParmas = {
    revenue: number | string; //商品价格总和
    trackItems: ITrackVariant[];
    storageItems: Array<IVariantNum>;
    refererType?: string;
    cacheKey?: string;
}

// 推荐列表
export type IRecommendListData = {
    variants: IProduct[];
    page?: number;
    pageSize?: number;
    totalNum?: number;
    totalPage?: number;
}

export type ITradeIn = {
    dealPrice: number;
    dealPriceDisplay: string;
    dealPriceExchange: number;
    dealPriceExchangeDisplay: string;
    free: boolean;
    points: number;
    selected: boolean;
    tradeInType: string;
    notSelectReason?: string;
}

export type IImageType = 'isMain' | 'isListHover'

export type IVariantNum = {
    variantId: string;
    num: number;
};

export type ITrackVariant = {
    // 所有类型商品都有的属性
    trackId?: string;
    productTrackCode?: string;
    variantTrackCode?: string;
    categoryTrackCode?: string;
    listTrackCode?: string;
    variantId?: string; //展示营销数据时需要定位到具体商品位置， ctr打点不需要
    // 商品列表属性
    dealPrice?: number;
    shopPrice?: number;
    // cart，checkout，order 商品属性
    id?: string; //下单流程中的variantId, list 接口中的 id 不用
    variantPrice?: number; //checkout及订单
    variantTotalPrice?: number; //checkout及订单
    num?: number;
    //非 api 数据
    position?: number | string;
    // item的埋点信息
    track?: {
        id: string; // 商品id : 变体ID
        name: string; // 商品埋点code
        variant: string; // 变体埋点code
        category: string; // 分类埋点code
        list: string; // 列表页的埋点code
    };
};

// 分类、推荐等商品列表中的单个商品
export type IProduct = ITrackVariant & {
    // id
    id: string;
    variantId: string;
    productId: string;
    // 价格
    shopPrice: number;
    shopPriceExchange: number;
    shopPriceExchangeDisplay: string;
    dealPrice: number;
    dealPriceExchange: number;
    dealPriceExchangeDisplay: string;
    dealerShopPriceExchangeDisplay: string; //wholesale用户划线价
    // discount 标签
    discountMode: 'none' | 'percent' | 'fixed'; // 优惠类型
    discountPriceNumDisplay: string; // 商品优惠值
    discountLocalPriceNumDisplay?: string; // 商品优惠值，老接口不一定存在
    // 图片等
    link: string;
    name: string;
    images: IImage[];
    productTags?: IProductTag[];
    isOnSale: number;
    isStock: number;
    activity: IProductActivity;
    points: {
        ratio: number;
        showTag: boolean;
        value: number;
    };
    reviewNum: number;
    reviewScore: string;
    disableActions: Array<'addToCart' | 'buyNow'>;
    picLink?: string; //filterProductsFields 转换后的值
    altText?: string; //filterProductsFields 转换后的值
    hoverAssetLink?: string; //filterProductsFields 转换后的值
    hoverAltText?: string; //filterProductsFields 转换后的值
    tag?: ProductTagType; //filterProductsFields 转换后的值
}

export enum TaskAction {
    Link = "link",
    None = "none",
}

export type ITaskItem = {
    icon: string;
    link: string;
    points: number;
    taskAction?: TaskAction;
    taskDone: boolean;
    taskName: string;
}
export type IProductShareTasks = {
    points: number;
    success: boolean;
    tasks: ITaskItem[];
}


